import handleError from '@fuse/core/errorHandler';
import { InfoOutlined } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import { setSignature } from 'app/store/deadlineDocumentSlice';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useMessage from 'src/app/hooks/useMessage';
import {
  useLazyGetDocumentByIdQuery,
  useGetDocumentsDeadLineQuery,
  useApproveDocumentDeadLineMutation,
} from 'src/app/services/claim/claimApi';
import VersionsDrawer from './components/VersionsDrawer';
import PdfRender from 'app/shared-components/PDFRender/PdfRender';
import FuseLoading from '@fuse/core/FuseLoading';

const MandatoryDocument = () => {
  const { t } = useTranslation('translations');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showMessage } = useMessage();

  const [documents, setDocuments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [document, setDocument] = useState(null);
  const [open, setOpen] = useState(false);
  const [isFetchedOnce, setIsFetchedOnce] = useState(false);

  const [getDocument, { isFetching, isError }] = useLazyGetDocumentByIdQuery();
  const [approveDocumentDeadLine] = useApproveDocumentDeadLineMutation();

  const { data: deadlineData } = useGetDocumentsDeadLineQuery(undefined, {
    skip: isFetchedOnce,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const isDeadlineHandled = useRef(false);

  useEffect(() => {
    if (deadlineData && !isFetchedOnce) {
      setIsFetchedOnce(true);
    }
  }, [deadlineData]);

  useEffect(() => {
    if (!deadlineData || isDeadlineHandled.current) return;

    isDeadlineHandled.current = true;

    if (deadlineData?.data?.length > 0) {
      setDocuments(deadlineData.data);
      loadDocument(deadlineData.data[0].id);
    } else {
      dispatch(setSignature(false));
      navigate('/');
    }
  }, [deadlineData]);

  useEffect(() => {
    if (isError && documents.length > 0) {
      const nextIndex = currentIndex + 1;
      if (nextIndex < documents.length) {
        setCurrentIndex(nextIndex);
        loadDocument(documents[nextIndex].id);
      } else {
        dispatch(setSignature(false));
        navigate('/');
      }
    }
  }, [isError]);

  async function loadDocument(id) {
    try {
      const data = await getDocument({ id }).unwrap();
      setDocument(data);
    } catch (error) {
      handleError(error);
    }
  }

  const handleSignDocument = async () => {
    if (!documents || documents.length === 0) return;

    const currentDoc = documents[currentIndex];
    if (!currentDoc) return;

    try {
      await approveDocumentDeadLine({ id: currentDoc.id }).unwrap();
      showMessage({ message: t('DocumentSuccessfullySigned'), variant: 'success' });

      const nextIndex = currentIndex + 1;
      if (nextIndex < documents.length) {
        setCurrentIndex(nextIndex);
        loadDocument(documents[nextIndex].id);
      } else {
        dispatch(setSignature(false));
        navigate('/');
      }
    } catch (error) {
      handleError(error);
    }
  };

  const showVersionsDrawer = () => setOpen(true);

  return (
    <div className="grid grid-rows-[60px_calc(100vh-190px)_60px]">
      <div className="flex items-center gap-10 p-10">
        <div className="hidden sm:block">
          <img
            className="h-32"
            src={`assets/images/logo/${process.env.REACT_APP_COMPANY_NAME}/logo-with-text.svg`}
            alt="logo"
          />
        </div>

        <div className="text-center w-full text-xs sm:text-sm md:text-lg lg:text-xl">
          {t('YouNeedToSignDocumentToContinueUsingSystem')}
        </div>
        <Tooltip placement="left" title={t('DocumentInformation')} arrow>
          <InfoOutlined
            className="text-[#2196f3] mr-5 cursor-pointer"
            onClick={showVersionsDrawer}
          />
        </Tooltip>
      </div>
      {isFetching && <FuseLoading />}
      {document && <PdfRender pdfFile={document} isFetching={isFetching} />}
      <div className="text-center my-10">
        <Button onClick={handleSignDocument} color="success" variant="contained">
          {document?.documentSignatureType === 1 ? t('ToFamiliarize') : t('ToSign')}
        </Button>
      </div>

      <VersionsDrawer open={open} onClose={() => setOpen(false)} data={document} />
    </div>
  );
};

export default MandatoryDocument;
